import Cookies from "js-cookie";
import AES from "crypto-js/aes";
import Utf8 from "crypto-js/enc-utf8";

// Encryption key - in production, this should be stored in environment variables
const ENCRYPTION_KEY =
  process.env.REACT_APP_COOKIE_ENCRYPTION_KEY ||
  "language-village-secure-zFIpSfLdv3ToOQhDlYGVLm2ed3AaPfzv66JwDUEcXvc=";

// Use NODE_ENV (built-in) or REACT_APP_ENV
const isProduction = process.env.NODE_ENV === "production" || 
                     process.env.REACT_APP_ENV === "production";

// Cookie configuration
const COOKIE_CONFIG = {
  expires: 7, // 7 days
  secure: isProduction, // Only send over HTTPS in production
  sameSite: "lax", // Protect against CSRF attacks
  path: "/", // Available across the entire site
  // IMPORTANT: Use REACT_APP_ prefix for React env vars, fallback to .language-village.org in production
  domain: process.env.REACT_APP_COOKIE_DOMAIN || 
          (isProduction ? ".language-village.org" : undefined),
};

/**
 * Encrypt data before storing in cookies
 * @param {any} data - Data to encrypt
 * @returns {string} - Encrypted string
 */
const encryptData = (data) => {
  try {
    const jsonString = JSON.stringify(data);
    return AES.encrypt(jsonString, ENCRYPTION_KEY).toString();
  } catch (error) {
    console.error("Encryption error:", error);
    throw new Error("Failed to encrypt data");
  }
};

/**
 * Decrypt data from cookies
 * @param {string} encryptedData - Encrypted string to decrypt
 * @returns {any} - Decrypted data
 */
const decryptData = (encryptedData) => {
  try {
    const bytes = AES.decrypt(encryptedData, ENCRYPTION_KEY);
    const decryptedString = bytes.toString(Utf8);
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error("Decryption error:", error);
    return null;
  }
};

/**
 * Set encrypted cookie
 * @param {string} name - Cookie name
 * @param {any} value - Value to store (will be encrypted)
 * @param {object} options - Additional cookie options
 */
export const setSecureCookie = (name, value, options = {}) => {
  try {
    const encryptedValue = encryptData(value);
    const cookieOptions = { ...COOKIE_CONFIG, ...options };

    Cookies.set(name, encryptedValue, cookieOptions);
  } catch (error) {
    console.error("Error setting secure cookie:", error);
    throw new Error("Failed to set secure cookie");
  }
};

/**
 * Get and decrypt cookie value
 * @param {string} name - Cookie name
 * @returns {any} - Decrypted value or null if not found/invalid
 */
export const getSecureCookie = (name) => {
  try {
    const encryptedValue = Cookies.get(name);
    if (!encryptedValue) return null;

    return decryptData(encryptedValue);
  } catch (error) {
    console.error("Error getting secure cookie:", error);
    return null;
  }
};

/**
 * Remove cookie
 * @param {string} name - Cookie name
 * @param {object} options - Additional cookie options
 */
export const removeSecureCookie = (name, options = {}) => {
  try {
    const cookieOptions = { ...COOKIE_CONFIG, ...options };
    Cookies.remove(name, cookieOptions);
  } catch (error) {
    console.error("Error removing secure cookie:", error);
  }
};

/**
 * Check if cookie exists
 * @param {string} name - Cookie name
 * @returns {boolean} - True if cookie exists
 */
export const hasSecureCookie = (name) => {
  return Cookies.get(name) !== undefined;
};

/**
 * Clear all secure cookies
 */
export const clearAllSecureCookies = () => {
  try {
    const allCookies = Cookies.get();
    Object.keys(allCookies).forEach((cookieName) => {
      Cookies.remove(cookieName, COOKIE_CONFIG);
    });
  } catch (error) {
    console.error("Error clearing all secure cookies:", error);
  }
};

/**
 * Set authentication data in secure cookies
 * @param {object} authData - Authentication data from API response
 */
export const setAuthCookies = (authData) => {
  try {
    // Store the entire response for backup/fallback
    setSecureCookie("auth_response", authData);

    // Store individual pieces for easier access
    if (authData.data) {
      setSecureCookie("user_token", authData.data.token);
      setSecureCookie("user_data", {
        uuid: authData.data.uuid,
        name: authData.data.name,
        email: authData.data.email,
        phone_number: authData.data.phone_number,
        username: authData.data.username,
        image_url: authData.data.image_url,
        lang: authData.data.lang,
        isInstructor: authData.data.isInstructor,
        isStudent: authData.data.isStudent,
        isSuperAdmin: authData.data.isSuperAdmin,
        twoFactorAuth: authData.data.twoFactorAuth,
        student_id: authData.data.student_id,
        instructor_id: authData.data.instructor_id,
      });
    }

    // Store API response metadata
    setSecureCookie("auth_metadata", {
      status: authData.status,
      errorCode: authData.errorCode,
      message: authData.message,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("Error setting auth cookies:", error);
    throw new Error("Failed to store authentication data");
  }
};

/**
 * Get authentication data from secure cookies
 * @returns {object|null} - Authentication data or null if not found
 */
export const getAuthCookies = () => {
  try {
    const authResponse = getSecureCookie("auth_response");
    if (authResponse) return authResponse;

    // Fallback: reconstruct from individual cookies
    const userToken = getSecureCookie("user_token");
    const userData = getSecureCookie("user_data");
    const authMetadata = getSecureCookie("auth_metadata");

    if (userToken && userData && authMetadata) {
      return {
        status: authMetadata.status,
        errorCode: authMetadata.errorCode,
        message: authMetadata.message,
        data: {
          token: userToken,
          ...userData,
        },
      };
    }

    return null;
  } catch (error) {
    console.error("Error getting auth cookies:", error);
    return null;
  }
};

/**
 * Clear authentication cookies
 */
export const clearAuthCookies = () => {
  try {
    removeSecureCookie("auth_response");
    removeSecureCookie("user_token");
    removeSecureCookie("user_data");
    removeSecureCookie("auth_metadata");
  } catch (error) {
    console.error("Error clearing auth cookies:", error);
  }
};

/**
 * Get user data from secure cookies
 * @returns {UserData|null} - User data or null if not found
 */
export const getUserData = () => {
  try {
    const authData = getAuthCookies();
    if (authData && authData.data) {
      return {
        uuid: authData.data.uuid,
        name: authData.data.name,
        email: authData.data.email,
        phone_number: authData.data.phone_number,
        username: authData.data.username,
        image_url: authData.data.image_url,
        lang: authData.data.lang,
        isInstructor: authData.data.isInstructor,
        isStudent: authData.data.isStudent,
        isSuperAdmin: authData.data.isSuperAdmin,
        twoFactorAuth: authData.data.twoFactorAuth,
      };
    }

    // Fallback: try to get user_data directly
    const userData = getSecureCookie("user_data");
    return userData;
  } catch (error) {
    console.error("Error getting user data:", error);
    return null;
  }
};

/**
 * Get authentication token from secure cookies
 * @returns {string|null} - Token or null if not found
 */
export const getAuthToken = () => {
  try {
    const authData = getAuthCookies();
    if (authData && authData.data && authData.data.token) {
      return authData.data.token;
    }

    // Fallback: try to get token directly
    const token = getSecureCookie("user_token");
    return token;
  } catch (error) {
    console.error("Error getting auth token:", error);
    return null;
  }
};

/**
 * Check if user is authenticated via cookies
 * @returns {boolean} - True if valid auth cookies exist
 */
export const isAuthenticatedViaCookies = () => {
  try {
    const authData = getAuthCookies();
    if (!authData || !authData.data || !authData.data.token) {
      return false;
    }

    // Check if token is not expired (basic check)
    const authMetadata = getSecureCookie("auth_metadata");
    if (authMetadata && authMetadata.timestamp) {
      const tokenAge = Date.now() - new Date(authMetadata.timestamp).getTime();
      const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

      if (tokenAge > maxAge) {
        clearAuthCookies();
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error("Error checking authentication via cookies:", error);
    return false;
  }
};
